import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  Wallet,
  MessageCircle,
  User,
  FileText,
  Shield,
  Bell,
  Search,
  Bookmark,
  LogOut,
  Settings,
  Lock,
  AlertCircle,
  Briefcase,
  FileCheck,
  Menu,
  X,
  ChevronRight,
  Globe,
  User2,
  Settings2,
  ChartAreaIcon,
  StarIcon,
  Verified,
  Loader2,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import PolicyModals from "./PolicyModals";
import SharedHeader5 from "../../Headers/SharedHeader5";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { MdReviews } from "react-icons/md";
import { LogoutManually, LogoutUser, useAppDispatch, getClientStats } from "./../../redux";
import ClientReviewsSection from "../../components/ui/ClientReviewsSection";
import { GiProgression } from "react-icons/gi";
import { useSelector } from "react-redux";
import SocialMediaLinks from "../../components/ui/SocialMediaLinks";
import { ContractStatusEnum } from "../../modules/shared/model/contract.enum";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
const InfoPopup = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl max-w-lg w-full p-6 relative"
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-xl font-semibold mb-4 text-center">{title}</h3>
        <div className="prose max-w-none text-center">{content}</div>
      </motion.div>
    </motion.div>
  );
};
const ClientDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showHiring, setShowHiring] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const { user, token } = useSelector((state: any) => state.auth);
  const { clientStats } = useSelector((state: any) => state.client);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Fetch client stats on component mount
  useEffect(() => {
    if (user?.id) {
      dispatch(getClientStats({ id: user.id, isShowLoader: false })).finally(() => {
        setIsDataLoading(false);
      });
    }
  }, [user?.id, dispatch]);

  // Check if data is loaded
  useEffect(() => {
    if (clientStats && Object.keys(clientStats).length > 0) {
      setIsDataLoading(false);
    }
  }, [clientStats]);
  const sidebarItems: any = [
    {
      icon: <Home className="h-5 w-5" />,
      label: t("postJob.dashboard"),
      path: "/clients",
      align: "center",
      rightIcon: <Home className="h-5 w-5" />,
    },
    {
      icon: <FileText className="h-5 w-5" />,
      label: t("postJob.postJobLocal"),
      path: "/clients/postajob",
      align: "center",
    },
    {
      icon: <FileText className="h-5 w-5" />,
      label: t("postJob.postJobInternational"),
      path: "/clients/postajob/international",
      align: "left",
    },
    {
      icon: <FileText className="h-5 w-5" />,
      label: t("postJob.findLocalProfessionals"),
      path: "/local-professionals",
      align: "center",
    },
    {
      icon: <FileText className="h-5 w-5" />,
      label: t("postJob.findInternationalProfessionals"),
      path: "/professional-international",
      align: "center",
    },

    {
      icon: <User2 className="h-5 w-5" />,
      label: t("postJob.myProfile"),
      path: "/client/profile",
      align: "left",
    },
    {
      icon: <User className="h-5 w-5" />,
      label: t("postJob.profileSettings"),
      path: "/client/profilesettings",
      align: "left",
    },
    // {
    //   icon: <Settings2 className="h-5 w-5" />,
    //   label: "My Settings",
    //   path: "/client/settings",
    //   align: "left",
    // },
    {
      icon: <FileCheck className="h-5 w-5" />,
      label: t("postJob.myOrders"),
      path: "/client/myorders",
      align: "left",
    },
    {
      icon: <Bell className="h-5 w-5" />,
      label: t("postJob.myOffers"),
      path: "/client/myoffers",
      align: "left",
    },
    {
      icon: <StarIcon className="h-5 w-5" />,
      label: t("postJob.favorites"),
      path: "/client/favorites",
      align: "left",
    },
    {
      icon: <MessageCircle className="h-5 w-5" />,
      label: t("postJob.messageOrVideoCall"),
      path: "/client/MessageAndVideoCall",
      align: "left",
    },
    {
      icon: <User className="h-5 w-5" />,
      label: t("postJob.report"),
      path: "/client/reports",
      align: "left",
    },
    {
      icon: <Wallet className="h-5 w-5" />,
      label: t("postJob.wallet"),
      path: "/client/Wallet",
      align: "left",
    },
    {
      icon: <ChartAreaIcon className="h-5 w-5" />,
      label: t("postJob.progress"),
      path: "/client/progress",
      align: "left",
    },
    {
      icon: <MdReviews className="h-5 w-5" />,
      label: t("postJob.reviews"),
      path: "/client/reviews",
      align: "left",
    },
    {
      icon: <Shield className="h-5 w-5" />,
      label: t("postJob.supportTickets"),
      path: "/client/support",
      align: "left",
    },
    {
      icon: <Verified className="h-5 w-5" />,
      label: t("postJob.contactUs"),
      path: "/menu/contact",
      align: "center",
    },
    {
      icon: <Verified className="h-5 w-5" />,
      label: t("postJob.refundRequest"),
      path: "/client/refund-requests",
      align: "center",
    },
    {
      icon: <Verified className="h-5 w-5" />,
      label: t("postJob.myInvoices"),
      path: "/client/invoices",
      align: "center",
    },
    {
      icon: <Verified className="h-5 w-5" />,
      label: t("postJob.verifyAccount"),
      path: "/verify",
      align: "center",
    },
    { icon: <Lock className="h-5 w-5" />, label: t("postJob.changePassword"), path: "/changepassword", align: "center" },
    {
      icon: <LogOut className="h-5 w-5" />,
      label: t("postJob.logOut"),
      path: "/logout",
      align: "left",
    },
  ];
  // Get stats from API data
  const stats = [
    {
      icon: <Wallet className="h-6 w-6 text-blue-600" />,
      bgColor: "bg-blue-100",
      label: t("clientDashboard.balance"),
      value: `${getDynamicCurrencySymbol()}${clientStats?.balance?.toFixed(2) || "0.00"}`,
      description: t("clientDashboard.availableInWallet"),
    },
    {
      icon: <Briefcase className="h-6 w-6 text-green-600" />,
      bgColor: "bg-green-100",
      label: t("clientDashboard.jobs"),
      value: clientStats?.jobsCount?.toString() || "0",
      description: t("clientDashboard.totalPostedJobs"),
    },
    {
      icon: <FileCheck className="h-6 w-6 text-yellow-600" />,
      bgColor: "bg-yellow-100",
      label: t("clientDashboard.complete"),
      value: clientStats?.completedContractsCount?.toString() || "0",
      description: t("clientDashboard.completedOrders"),
    },
    {
      icon: <AlertCircle className="h-6 w-6 text-purple-600" />,
      bgColor: "bg-purple-100",
      label: t("clientDashboard.active"),
      value: clientStats?.activeContractsCount?.toString() || "0",
      description: t("clientDashboard.activeOrders"),
    },
  ];

  // Get contracts (orders) from API data
  const orders = clientStats?.contracts || [];

  // Get jobs from API data
  const jobs = clientStats?.jobs || [];

  // Helper function to get status badge color
  const getStatusColor = (statusId: number) => {
    switch (statusId) {
      case ContractStatusEnum.Completed:
        return "bg-green-100 text-green-800";
      case ContractStatusEnum.Active:
      case ContractStatusEnum.Accepted:
        return "bg-blue-100 text-blue-800";
      case ContractStatusEnum.Pending:
        return "bg-yellow-100 text-yellow-800";
      case ContractStatusEnum.Cancelled:
      case ContractStatusEnum.Rejected:
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  // Helper function to calculate delivery time from milestone dueDate
  const calculateDeliveryTime = (milestones: any[]) => {
    if (!milestones || milestones.length === 0 || !milestones[0]?.dueDate) {
      return "N/A";
    }

    const dueDate = new Date(milestones[0].dueDate);
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return "Overdue";
    } else if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "1 day";
    } else if (diffDays < 7) {
      return `${diffDays} days`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? "1 week" : `${weeks} weeks`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? "1 month" : `${months} months`;
    } else {
      const years = Math.floor(diffDays / 365);
      return years === 1 ? "1 year" : `${years} years`;
    }
  };
  const handleLogout = async () => {
    try {
      const response = await dispatch(LogoutUser({})).unwrap();
    } catch (error) {
      if (error === 401) {
        dispatch(LogoutManually());
        console.log("Token expired");
      } else {
        console.log("Logout failed:", error);
      }
    }
  };

  const StoreLinks = () => (
    <div className="flex justify-center gap-2 pt-6 px-4">
      <a
        href="https://play.google.com/store/apps/details?id=com.kokobeo"
        className="w-1/2"
      >
        <img
          src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
          alt="Get it on Google Play"
          className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
          style={{ maxWidth: "160px", height: "50px", objectFit: "contain" }}
        />
      </a>
      <a href="https://apps.apple.com/app/kokobeo" className="w-1/2">
        <img
          src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
          alt="Download on App Store"
          className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
          style={{ maxWidth: "160px", height: "50px", objectFit: "contain" }}
        />
      </a>
    </div>
  );
  const SocialIcons = () => (
    <SocialMediaLinks className="flex justify-center space-x-4 pt-4 px-0 pb-6" iconSize="h-4 w-4" />
  );

  // Loading component - transparent overlay
  const LoadingSpinner = () => (
    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 shadow-lg flex items-center space-x-3">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        <span className="text-gray-700 font-medium">{t("clientDashboard.loadingDashboard")}</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <SharedHeader5 />
      <div className="flex flex-1 min-h-[calc(100vh-64px)] pt-16">
        <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white border-r overflow-y-auto h-[calc(100vh-64px)]">
          <nav className="p-4 space-y-1 h-full flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {sidebarItems.map((item, index) => item.path === "/logout" ? (
                <button
                  key={index}
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ) : item.rightIcon ? (
                <div key={index} className="flex items-center justify-between px-4 py-3 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 ${location.pathname === item.path
                      ? "text-blue-600 bg-blue-50 font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                      } ${item.align === "center" ? "justify-center-important" : ""
                      }`}
                  >
                    <span
                      className="flex items-center gap-3"
                      style={
                        item.align === "center"
                          ? { justifyContent: "center !important" }
                          : { justifyContent: "flex-start" }
                      }
                    >
                      {item.icon}
                      {item.label}
                    </span>
                  </Link>
                  <button
                    onClick={() => navigate("/")}
                    className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Go to Home"
                  >
                    <Home className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Link
                  key={index}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${location.pathname === item.path
                    ? "text-blue-600 bg-blue-50 font-medium"
                    : "text-gray-600 hover:bg-gray-50"
                    } ${item.align === "center" ? "justify-center-important" : ""
                    }`}
                >
                  <span
                    className="flex items-center gap-3"
                    style={
                      item.align === "center"
                        ? { justifyContent: "center !important" }
                        : { justifyContent: "flex-start" }
                    }
                  >
                    {item.icon}
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-auto">
              <StoreLinks />
              <SocialIcons />
            </div>
          </nav>
        </aside>
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              />
              <motion.aside
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                className="fixed inset-y-0 left-0 w-full max-w-xs bg-white shadow-xl z-50 lg:hidden overflow-y-auto h-[calc(100vh-64px)]"
              >
                <div className="p-4 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <img
                        src="https://assests.netlify.app/assets/images/logo.png"
                        alt="Logo"
                        className="h-8 w-8"
                      />
                      <span className="text-blue-600 text-lg font-bold">
                        Kokobeo
                      </span>
                    </div>
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  <nav className="space-y-1 flex-1 overflow-y-auto">
                    {sidebarItems.map((item, index) => item.path === "/logout" ? (
                      <button
                        key={index}
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                        {item.icon}
                        <span>{item.label}</span>
                      </button>
                    ) : item.rightIcon ? (
                      <div key={index} className="flex items-center justify-between px-4 py-3 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                        <Link
                          to={item.path}
                          className={`flex items-center gap-3 ${location.pathname === item.path
                            ? "text-blue-600 bg-blue-50 font-medium"
                            : "text-gray-600 hover:bg-gray-50"
                            } ${item.align === "center"
                              ? "justify-center-important"
                              : ""
                            }`}
                          style={
                            item.align === "center"
                              ? { justifyContent: "center !important" }
                              : { justifyContent: "flex-start" }
                          }
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span className="flex items-center gap-3">
                            {item.icon}
                            {item.label}
                          </span>
                        </Link>
                        <button
                          onClick={() => {
                            navigate("/");
                            setIsMobileMenuOpen(false);
                          }}
                          className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Go to Home"
                        >
                          <Home className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <Link
                        key={index}
                        to={item.path}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${location.pathname === item.path
                          ? "text-blue-600 bg-blue-50 font-medium"
                          : "text-gray-600 hover:bg-gray-50"
                          } ${item.align === "center"
                            ? "justify-center-important"
                            : ""
                          }`}
                        style={
                          item.align === "center"
                            ? { justifyContent: "center !important" }
                            : { justifyContent: "flex-start" }
                        }
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="flex items-center gap-3">
                          {item.icon}
                          {item.label}
                        </span>
                      </Link>
                    ))}
                  </nav>
                  <div className="mt-auto">
                    <StoreLinks />
                    <SocialIcons />
                  </div>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
        <div className="flex-1 w-full lg:pl-64 flex flex-col">
          <main className="flex-1 p-4 lg:p-8 overflow-y-auto w-full">
            <div className="max-w-7xl mx-auto space-y-8 w-full">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {t("clientDashboard.welcomeBack", { firstName: user?.firstName || user?.email?.split("@")[0] || "User" })}
                  </h1>
                  <p className="text-gray-600 mt-1">
                    {t("clientDashboard.happeningToday")}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
                {stats.map((stat, index) => (
                  <Card
                    key={index}
                    className={`${stat.bgColor} p-6 transition-all hover:shadow-lg w-full`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                        {stat.icon}
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {stat.description}
                    </div>
                  </Card>
                ))}
              </div>
              <Card className="overflow-hidden w-full">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b gap-4 w-full">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {t("clientDashboard.latestOrders")}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("clientDashboard.trackAndManage")}
                    </p>
                  </div>
                  <Button
                    className="w-full sm:w-auto"
                    onClick={() => {
                      localStorage.setItem("myOrdersSelectedTab", "active");
                      navigate("/client/myorders");
                    }}
                  >
                    {t("clientDashboard.viewAllOrders")}
                  </Button>
                </div>
                <div className="overflow-x-auto w-full">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200 w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {t("clientDashboard.budget")}
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {t("clientDashboard.deliveryTime")}
                          </th>
                          <th
                            scope="col"
                            className="hidden md:table-cell px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {t("clientDashboard.status")}
                          </th>
                          <th
                            scope="col"
                            className="hidden lg:table-cell px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {t("clientDashboard.date")}
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {t("clientDashboard.action")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {orders.length > 0 ? (
                          orders.slice(0, 5).map((order: any, index: number) => (
                            <tr
                              key={order.id || index}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {getDynamicCurrencySymbol()}{order.amount?.toFixed(2) || "0.00"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {calculateDeliveryTime(order.milestones)}
                              </td>
                              <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(Number(order.status?.id))}`}>
                                  {order.status?.name || "Unknown"}
                                </span>
                              </td>
                              <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {formatDate(order.createdAt)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    localStorage.setItem("myOrdersSelectedTab", "active");
                                    navigate("/client/myorders");
                                  }}
                                >
                                  {t("clientDashboard.viewDetails")}
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                              {t("clientDashboard.noOrdersFound")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
              <Card className="overflow-hidden w-full">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b gap-4 w-full">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {t("clientDashboard.latestJobs")}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("clientDashboard.activeJobListings")}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      localStorage.setItem("myOrdersSelectedTab", "requests");
                      navigate("/client/myorders");
                    }}
                  >
                    {t("clientDashboard.viewAllJobs")}
                  </Button>
                </div>
                <div className="overflow-x-auto w-full">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200 w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {t("clientDashboard.title")}
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {t("clientDashboard.action")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {jobs.length > 0 ? (
                          jobs.slice(0, 5).map((job: any, index: number) => (
                            <tr
                              key={job.id || index}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-6 py-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {job.title}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {job.description?.substring(0, 60)}
                                  {job.description?.length > 60 ? "..." : ""}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(job.isLocal ? `/clients/postajob/${job.id}` : `/clients/postajob/international/${job.id}`)}
                                >
                                  {t("clientDashboard.editJob")}
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={2} className="px-6 py-8 text-center text-sm text-gray-500">
                              {t("clientDashboard.noJobsFound")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            </div>
          </main>
          <footer className="bg-white border-t w-full mt-auto">
            <div className="max-w-7xl mx-auto">
              {!isDataLoading && <SharedFooter3 />}
            </div>
          </footer>
        </div>
      </div>
      <PolicyModals
        showPrivacyModal={showPrivacyModal}
        showTermsModal={showTermsModal}
        onClosePrivacy={() => setShowPrivacyModal(false)}
        onCloseTerms={() => setShowTermsModal(false)} showProfessionalTermsModal={undefined} onCloseProfessionalTerms={undefined} />

      {/* Transparent loading overlay */}
      {isDataLoading && <LoadingSpinner />}
    </div>
  );
};
export default ClientDashboard;